/**
 * SecondaryNav — Per-pillar secondary tab bar (underline-tab style).
 *
 * Renders the tabs defined in navConfig for the active pillar.
 * Feature-gated tabs are automatically hidden.
 *
 * Progressive disclosure (Command pillar, sub role only):
 * For subs (role === 'member'), conditional tabs (Protocols, Chastity, Travel,
 * Reviews, Economy) are hidden when they have no data. A "More available"
 * indicator appears when at least one tab is hidden, so the sub knows features
 * exist. Doms always see all tabs.
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useTerminology, type TerminologyKey } from '../../hooks/useTerminology';
import { getActivePillar, getPillarConfig, getCommandTabs, type PillarId, type PillarTab } from './navConfig';
import { useSubCommandTabPresence } from '../../hooks/queries/useSubCommandTabPresence';
import { useUnreadMessageCount } from '../../hooks/queries/useUnreadMessageCount';
import { isSubRole } from '../../utils/roleHelpers';
/** Keys that get an 's' suffix when pluralized for tab labels */
const PLURALIZE_KEYS: Record<string, boolean> = {
  task: true, reward: true, consequence: true, protocol: true, ritual: true,
};

function pluralizeLabel(key: string, singular: string): string {
  if (!PLURALIZE_KEYS[key]) return singular;
  return singular + 's';
}

interface SecondaryNavProps {
  /** When set, renders this pillar's tabs even if the URL doesn't match. */
  overridePillarId?: PillarId | null;
}

const SecondaryNav: React.FC<SecondaryNavProps> = ({ overridePillarId }) => {
  const { pathname } = useLocation();
  const { features, tabOrder } = useFeatures();
  const { organization } = useOrganization();
  const { t, loading: terminologyLoading } = useTerminology();
  const activePillarId = overridePillarId || getActivePillar(pathname);
  const navRef = useRef<HTMLElement>(null);
  const [scrollLeft, setScrollLeft] = useState(false);
  const [scrollRight, setScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const canScrollLeft = el.scrollLeft > 16;
    const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 16;
    setScrollLeft(canScrollLeft);
    setScrollRight(canScrollRight);
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    // Delay initial measurement to ensure DOM is fully rendered and measured
    const raf = requestAnimationFrame(() => {
      updateScrollIndicators();
      // Also check after fonts/layout settle
      setTimeout(updateScrollIndicators, 200);
    });
    el.addEventListener('scroll', updateScrollIndicators, { passive: true });
    window.addEventListener('resize', updateScrollIndicators);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [updateScrollIndicators, activePillarId]);

  const isCommandPillar = activePillarId === 'command';
  const isConnectPillar = activePillarId === 'connect';
  const isSubViewer = isSubRole(organization?.role);
  // Notification-prominence follow-up (2026-08-15) — same queryKey as
  // MobileBottomNav.tsx's badge (both always-mounted), so TanStack Query
  // dedupes this to the one shared cached request, not a second poll.
  const unreadMessageCount = useUnreadMessageCount();
  // NounSeg mode: Command pillar → thicker underline + tighter spacing
  const nounSegMode = isCommandPillar;

  const featureDiscoveryMode = !!(features as unknown as Record<string, unknown>).featureDiscoveryMode;

  // Progressive disclosure applies only for subs in Command
  // AND the dom has NOT enabled Feature Discovery Mode (which shows all tabs).
  const presenceEnabled = isCommandPillar && isSubViewer && !featureDiscoveryMode;

  // Presence hook — no-ops (all true) when presenceEnabled is false
  const { tabPresence } = useSubCommandTabPresence({
    orgId: organization ? String(organization.id) : undefined,
    enabled: presenceEnabled,
  });

  // The tab IDs that are subject to progressive disclosure for subs
  const PRESENCE_GATED_TAB_IDS: Record<string, keyof typeof tabPresence> = {
    protocols: 'protocols',
    chastity: 'chastity',
    travel: 'travel',
    reviews: 'reviews',
    economy: 'economy',
  };

  if (!activePillarId) return null;

  const pillar = getPillarConfig(activePillarId);
  if (!pillar) return null;

  // Command R4: Use role-specific tabs
  const baseTabs = isCommandPillar
    ? getCommandTabs(organization?.role || 'member')
    : pillar.tabs;

  // Filter out feature-gated tabs where the flag is falsy, and hidden tabs.
  // MUST stay reference-preserving (.filter only, never .map/spread into new
  // tab objects) -- the custom-order logic below (bug-registry 0zzz-by)
  // tracks placed tabs by OBJECT IDENTITY, not tabId, specifically so a tab
  // with no tabId still gets appended correctly. Re-creating tab objects
  // anywhere in this chain would silently break that.
  const enabledTabs = baseTabs.filter((tab: PillarTab) => {
    if (tab.hideInV2) return false;
    if (!tab.featureGate) return true;
    return !!(features as unknown as Record<string, unknown>)[tab.featureGate];
  });

  // Progressive disclosure: hide presence-gated tabs for subs when no data
  const hiddenByPresenceCount = presenceEnabled
    ? enabledTabs.filter((tab: PillarTab) => {
        const presenceKey = tab.tabId ? PRESENCE_GATED_TAB_IDS[tab.tabId] : undefined;
        if (!presenceKey) return false;
        return !tabPresence[presenceKey];
      }).length
    : 0;

  const presenceFilteredTabs = presenceEnabled
    ? enabledTabs.filter((tab: PillarTab) => {
        const presenceKey = tab.tabId ? PRESENCE_GATED_TAB_IDS[tab.tabId] : undefined;
        if (!presenceKey) return true; // always-visible tabs (Directives, Structure)
        return tabPresence[presenceKey];
      })
    : enabledTabs;

  // Apply custom tab order if set for this pillar
  const pillarOrder = tabOrder?.[activePillarId];
  let visibleTabs: PillarTab[];
  if (pillarOrder && Array.isArray(pillarOrder)) {
    // Tabs in the custom order (preserving order, skipping disabled)
    const orderedTabs: PillarTab[] = [];
    // bug-registry 0zzz-by: tracked by OBJECT IDENTITY, not tabId. A tab with
    // no tabId can never match the pillarOrder lookup below, so it's never
    // added here -- tracking "placed" by tabId alone meant the leftover pass
    // (which required `tab.tabId &&` to append) skipped it too, and it
    // vanished from the nav entirely once any custom order was set for the
    // pillar. Identity tracking makes the leftover pass correct for every
    // tab regardless of whether it has an id.
    const placedTabs = new Set<PillarTab>();
    for (const tabId of pillarOrder) {
      const tab = presenceFilteredTabs.find(t => t.tabId === tabId);
      if (tab) {
        orderedTabs.push(tab);
        placedTabs.add(tab);
      }
    }
    // Tabs not in the custom order appear at the end (safe default for new features)
    for (const tab of presenceFilteredTabs) {
      if (!placedTabs.has(tab)) {
        orderedTabs.push(tab);
      }
    }
    visibleTabs = orderedTabs;
  } else {
    visibleTabs = presenceFilteredTabs;
  }

  return (
    <div data-secondary-nav="true" className="sticky top-[var(--navbar-height,73px)] z-[98] md:top-[var(--navbar-height,52px)] bg-[var(--theme-bg-elevated)] md:bg-[var(--theme-bg-primary)]">
      {scrollLeft && (
        <span
          className="absolute top-0 bottom-0 left-0 w-7 z-[99] flex items-center justify-center text-sm font-bold text-[var(--theme-brand-bronze)] pointer-events-none bg-gradient-to-r from-[var(--theme-bg-elevated)] via-[var(--theme-bg-elevated)] to-transparent md:from-[var(--theme-bg-primary)] md:via-[var(--theme-bg-primary)]"
          aria-hidden="true"
        >
          &laquo;
        </span>
      )}
      <nav
        ref={navRef}
        className="bg-[var(--theme-bg-elevated)] md:bg-[var(--theme-bg-primary)] md:border-b md:border-border-light overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [scroll-snap-type:x_mandatory] [&::-webkit-scrollbar]:hidden"
        data-testid="secondary-nav"
        role="navigation"
        aria-label={`${pillar.label} navigation`}
        style={{
          '--pillar-accent': `var(${pillar.accentVar})`,
          background: 'color-mix(in srgb, var(--pillar-accent) 6%, var(--theme-bg-elevated))',
        } as React.CSSProperties}
      >
        <div className="flex min-w-max px-3 md:px-6 md:max-w-[1200px] md:mx-auto">
          {visibleTabs.map((tab) => {
            // Exact match OR any sub-route/dynamic segment nested under this tab.
            // Root tab (baseRoute) is excluded from prefix matching to prevent
            // /command matching /command/structure, /command/protocols, etc.
            const isExact =
              pathname === tab.to ||
              (tab.to !== pillar.baseRoute && pathname.startsWith(tab.to + '/'));
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === pillar.baseRoute}
                className={`py-2.5 px-3 flex items-center font-medium no-underline border-transparent transition-colors whitespace-nowrap shrink-0 [scroll-snap-align:start] md:px-4 md:py-2 md:block ${
                  nounSegMode
                    ? `text-sm border-b-[3px] ${isExact ? 'text-[var(--pillar-accent,var(--theme-primary))] !border-b-[var(--pillar-accent,var(--theme-primary))] font-semibold' : 'text-text-secondary hover:text-text-primary'}`
                    : `text-sm border-b-2 ${isExact ? 'text-[var(--pillar-accent,var(--theme-primary))] !border-b-[var(--pillar-accent,var(--theme-primary))]' : 'text-text-secondary hover:text-text-primary'}`
                }`}
                aria-current={isExact ? 'page' : undefined}
              >
                {tab.terminologyKey && !terminologyLoading
                  ? pluralizeLabel(tab.terminologyKey, t(tab.terminologyKey as TerminologyKey))
                  : tab.label}
                {isConnectPillar && tab.tabId === 'messages' && unreadMessageCount > 0 && (
                  <span
                    className="inline-flex ml-1.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold items-center justify-center bg-accent text-white align-middle"
                    aria-label={`${unreadMessageCount} unread message${unreadMessageCount === 1 ? '' : 's'}`}
                  >
                    {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                  </span>
                )}
              </NavLink>
            );
          })}
          {/* Progressive disclosure indicator — subs only, shown when tabs are hidden */}
          {hiddenByPresenceCount > 0 && (
            <span
              className="self-center ml-1 mr-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap shrink-0 border border-[var(--theme-border-light)] text-text-tertiary bg-[var(--theme-bg-tertiary)] select-none"
              title={`${hiddenByPresenceCount} more feature${hiddenByPresenceCount > 1 ? 's' : ''} unlock as your dominant assigns content`}
              aria-label={`${hiddenByPresenceCount} additional feature${hiddenByPresenceCount > 1 ? 's' : ''} will appear once your dominant assigns content`}
            >
              +{hiddenByPresenceCount} more
            </span>
          )}
        </div>
      </nav>
      {scrollRight && (
        <span
          className="absolute top-0 bottom-0 right-0 w-7 z-[99] flex items-center justify-center text-sm font-bold text-[var(--theme-brand-bronze)] pointer-events-none bg-gradient-to-l from-[var(--theme-bg-primary)] via-[var(--theme-bg-primary)] to-transparent"
          aria-hidden="true"
        >
          &raquo;
        </span>
      )}
    </div>
  );
};

export default SecondaryNav;
